/**
 * <Term> — inline glossary popover.
 *
 * Renders a glossary term from `glossary.ts` with a tooltip that opens on
 * hover (desktop) and on tap (mobile).  Keyboard-accessible: focus opens,
 * blur or Escape closes.
 *
 * Author syntax in prose: `[[term-id]]` (label from glossary) or
 * `[[term-id|custom text]]` (custom surface text, same definition).  The
 * Renderer parses these markers and mounts <Term> in their place.
 *
 * Visual register: ink-900 underline, dotted (so reader knows it is
 * interactive), terra-600 on hover.  Popover is paper-2 with ink-900 text
 * and a 1 px border; mobile centres on screen with a backdrop.
 */

import { useEffect, useRef, useState } from 'react';
import type { LangCode } from './architecture';
import { getGlossaryEntry } from './glossary';

interface TermProps {
  /** Glossary id (kebab-case). */
  id: string;
  /** Optional custom surface text; defaults to the glossary label. */
  surface?: string;
  /** Current article language; determines which side of the glossary entry is shown. */
  lang: LangCode;
}

export function Term({ id, surface, lang }: TermProps) {
  const entry = getGlossaryEntry(id, lang);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  // Outside-click + Escape close. Declared before the early return to satisfy
  // Rules of Hooks — the guard `if (!open) return` inside is sufficient.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Author-error safety: unknown id → render plain text with a console warning
  // (dev only) so reviewers can spot missing glossary entries.
  if (!entry) {
    if (import.meta.env.DEV) {
      console.warn(`[Term] Unknown glossary id "${id}"`); // dev-only warning
    }
    return <span className="text-rust-600 underline decoration-dotted">{surface ?? id}</span>;
  }

  const label = surface ?? entry.label;
  const closedLabel = lang === 'ru' ? 'Определение' : 'Definition';

  return (
    <span ref={containerRef} className="group relative inline">
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${closedLabel}: ${entry.label}`}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="cursor-help border-0 bg-transparent p-0 font-inherit text-ink-900 underline decoration-terra-600/60 decoration-dotted underline-offset-[3px] transition-colors hover:text-terra-700 hover:decoration-terra-600 focus:outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-terra-600/60"
      >
        {label}
      </button>

      <span
        role="tooltip"
        aria-hidden={!open}
        className={`absolute left-1/2 top-full z-50 mt-2 w-[min(22rem,80vw)] -translate-x-1/2 rounded-lg border border-ink-100 bg-paper px-3.5 py-3 text-left text-sm leading-snug text-ink-800 shadow-lg transition-opacity duration-150 motion-reduce:transition-none ${
          open ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        <span className="mb-1 block font-display text-base font-medium italic text-ink-900">
          {entry.label}
        </span>
        <span className="block text-[13px] leading-relaxed text-ink-700">
          {entry.definition}
        </span>
      </span>
    </span>
  );
}
